// Autopilot `task_complete` handling shared by every AHP turn renderer.
//
// Since copilot-host 0.9.1 an autopilot turn ends with a `task_complete` tool
// call (the runtime keeps prompting until the model calls it). It is a control
// signal, not content: it never renders as a tool row. Only the last successful
// call in a turn matters; its summary is shown as assistant text only when the
// turn has no final assistant text of its own.

import type {ResponsePart, ToolCallState} from '@microsoft/agent-host-protocol'

import {inlineToolInput} from './tool-input'

export const TASK_COMPLETE_TOOL_NAME = 'task_complete'

// AHP enums are const, so compare wire literals (isolatedModules).
const PART_MARKDOWN = 'markdown'
const PART_REASONING = 'reasoning'
const PART_TOOL_CALL = 'toolCall'
const PART_INPUT_REQUEST = 'inputRequest'
const STATUS_COMPLETED = 'completed'

type ToolCallPart = Extract<ResponsePart, {kind: typeof PART_TOOL_CALL}>

// Not a type guard: its false branch would otherwise narrow every tool call away.
export function isTaskCompletePart(part: ResponsePart | undefined): boolean {
  return part?.kind === PART_TOOL_CALL && part.toolCall.toolName === TASK_COMPLETE_TOOL_NAME
}

/** The trimmed `summary` argument, once `toolCallReady` has delivered the input. */
export function getTaskCompleteSummary(toolCall: ToolCallState): string | undefined {
  const raw = inlineToolInput('toolInput' in toolCall ? toolCall.toolInput : undefined)
  if (!raw) return undefined
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed === 'object' && parsed !== null && 'summary' in parsed && typeof parsed.summary === 'string') {
      const summary = parsed.summary.trim()
      return summary || undefined
    }
  } catch {
    // Malformed input carries no usable summary.
  }
  return undefined
}

/**
 * Only a call that completed with `success: true` counts. The runtime rejects a
 * premature completion with `success: false`, so a running call may still fail
 * and must render nothing until it completes.
 */
function isSuccessfulTaskComplete(toolCall: ToolCallState): boolean {
  return toolCall.status === STATUS_COMPLETED && toolCall.success === true
}

export interface TaskCompletionResolution {
  /** Index of the last successful `task_complete` part, if any. */
  readonly completionIndex?: number
  /** Set when the summary is the turn's answer and should render as assistant text at `completionIndex`. */
  readonly answerSummary?: string
}

/** Whether a non-`task_complete` part renders something in the timeline. */
export type ResponsePartVisibility = (part: ResponsePart, index: number) => boolean

/**
 * Tool calls the AHP timeline never renders as rows (they have dedicated UI or
 * are invisible). The timeline still shows a `task` call that has a subagent;
 * callers without that context treat it as hidden.
 */
export const TOOLS_HIDDEN_FROM_AHP_TIMELINE: ReadonlySet<string> = new Set([
  'task',
  'report_intent',
  'ask_user',
  'exit_plan_mode',
  'permission_request',
])

// Matches the timeline for callers (export, previews) that have no subagent context.
const defaultVisibility: ResponsePartVisibility = part => {
  if (part.kind === PART_MARKDOWN) return Boolean(part.content?.trim())
  if (part.kind === PART_TOOL_CALL) return !TOOLS_HIDDEN_FROM_AHP_TIMELINE.has(part.toolCall.toolName)
  return part.kind === PART_INPUT_REQUEST
}

const EMPTY_RESOLUTION: TaskCompletionResolution = {}

/**
 * Picks the last successful `task_complete` and decides whether its summary
 * is redundant (the last visible part before it is assistant text) or the
 * answer (a visible tool call or input request comes last, or nothing does).
 */
export function resolveTaskCompletion(
  parts: readonly ResponsePart[],
  isVisible: ResponsePartVisibility = defaultVisibility,
): TaskCompletionResolution {
  let completionIndex = -1
  for (let index = parts.length - 1; index >= 0; index--) {
    const part = parts[index]
    if (part?.kind === PART_TOOL_CALL && isTaskCompletePart(part) && isSuccessfulTaskComplete(part.toolCall)) {
      completionIndex = index
      break
    }
  }
  if (completionIndex < 0) return EMPTY_RESOLUTION

  const completionPart = parts[completionIndex] as ToolCallPart
  for (let index = completionIndex - 1; index >= 0; index--) {
    const part = parts[index]
    if (!part) continue
    if (part.kind === PART_REASONING || isTaskCompletePart(part) || !isVisible(part, index)) continue
    if (part.kind === PART_MARKDOWN && part.content?.trim()) return {completionIndex}
    break
  }

  const answerSummary = getTaskCompleteSummary(completionPart.toolCall)
  return answerSummary ? {completionIndex, answerSummary} : {completionIndex}
}

/**
 * Assistant text blocks in display order: non-empty markdown parts plus the
 * `task_complete` summary when it is the answer. Used by export and previews
 * so they match the timeline.
 */
export function getAssistantTextBlocks(
  parts: readonly ResponsePart[],
  resolution: TaskCompletionResolution = resolveTaskCompletion(parts),
): string[] {
  const blocks: string[] = []
  for (const [index, part] of parts.entries()) {
    if (part.kind === PART_MARKDOWN) {
      if (part.content) blocks.push(part.content)
    } else if (index === resolution.completionIndex && resolution.answerSummary) {
      blocks.push(resolution.answerSummary)
    }
  }
  return blocks
}

/**
 * Text for "Copy response". The summary alone when it is the answer and no
 * visible tool call follows it; otherwise every markdown block.
 */
export function getCopyableResponseText(
  parts: readonly ResponsePart[],
  isVisible: ResponsePartVisibility = defaultVisibility,
  resolution: TaskCompletionResolution = resolveTaskCompletion(parts, isVisible),
): string {
  const {completionIndex, answerSummary} = resolution
  if (completionIndex !== undefined && answerSummary) {
    const followedByVisibleToolCall = parts.some(
      (part, index) =>
        index > completionIndex && part.kind === PART_TOOL_CALL && !isTaskCompletePart(part) && isVisible(part, index),
    )
    if (!followedByVisibleToolCall) return answerSummary
  }

  return parts.flatMap(part => (part.kind === PART_MARKDOWN && part.content ? [part.content] : [])).join('\n\n')
}
